import { useEffect, useRef, useState } from 'react'

type Handlers = { onStop?: () => void; onEnd?: () => void }

// 监听每轮计时,在「进入停止抓牌窗口」和「计时归零」两个时刻各触发一次回调。
// - date: 本轮结束的绝对时间戳(ms);<=0 表示未开始/无计时
// - stopWindowMs: 距结束多久算「停止抓牌」(= round_stop_info_time 分钟)
// - enabled: 是否启用(可被开关关掉)
// 关键:每次挂载/换轮时按「当前已处于哪个阶段」初始化已触发标记,
//       这样中途打开大屏或页面刷新不会立刻乱响,只有真正跨过阈值才触发。
export function useTimerAlerts(date: number, stopWindowMs: number, enabled: boolean, handlers: Handlers): void {
  const onStopRef = useRef(handlers.onStop)
  const onEndRef = useRef(handlers.onEnd)
  onStopRef.current = handlers.onStop
  onEndRef.current = handlers.onEnd

  const firedStop = useRef(true)
  const firedEnd = useRef(true)

  useEffect(() => {
    if (!enabled || !date || date <= 0) return undefined

    const now = Date.now()
    // 已结束 → 两个都视为已触发;已在停止窗口内 → 结束仍待触发、停止预警不再补响
    firedEnd.current = now >= date
    firedStop.current = firedEnd.current || date - now <= stopWindowMs

    const check = () => {
      const t = Date.now()
      if (t >= date) {
        if (!firedEnd.current) {
          firedEnd.current = true
          onEndRef.current?.()
        }
        return
      }
      if (date - t <= stopWindowMs && !firedStop.current) {
        firedStop.current = true
        onStopRef.current?.()
      }
    }

    const id = window.setInterval(check, 300)
    return () => window.clearInterval(id)
  }, [date, stopWindowMs, enabled])
}

const STORAGE_KEY = 'lt-timer-alert'

// 提示音/特效开关,持久化到本设备 localStorage,默认开启。
export function useTimerAlertEnabled(): [boolean, (v: boolean) => void] {
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(STORAGE_KEY)
      if (v === '0') setEnabled(false)
    } catch {
      // ignore
    }
  }, [])

  const update = (v: boolean) => {
    setEnabled(v)
    try {
      window.localStorage.setItem(STORAGE_KEY, v ? '1' : '0')
    } catch {
      // ignore
    }
  }

  return [enabled, update]
}
